import React, { useContext, useState } from 'react';
import { Popover, Tooltip, Box, Typography, Button, useMediaQuery } from '@mui/material';
import { Source } from '../../lib/models/Chat';
import './ChatComponents.css';
import { urlContext, highlightTextContext, sourceContext, citationViewerOpenContext } from './CitationViewer'

interface CitationReferenceProps {
    numbers: number[];
    citationSources?: (Source | undefined)[];
}

export const CitationReference: React.FC<CitationReferenceProps> = ({ numbers, citationSources = [] }) => {
    const { setUrl } = useContext(urlContext);
    const { setHighlightText } = useContext(highlightTextContext);
    const { setSource } = useContext(sourceContext);
    const { setCitationViewerOpen } = useContext(citationViewerOpenContext);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const validSources = citationSources.filter((s): s is Source => !!s);
    const hasSource = validSources.length > 0;
    const popoverOpen = Boolean(anchorEl) && hasSource;
    const badgeText = `[${numbers.join(', ')}]`;

    const handleOpenDocViewer = (source: Source) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setUrl(source.presigned_url ?? '');
        setHighlightText(source.chunk_content ?? '');
        setSource(source);
        setCitationViewerOpen(true);
    };

    const [selectedIndex, setSelectedIndex] = useState(0);

    const activeNumber = numbers[selectedIndex] ?? numbers[0];
    const activeSource = citationSources?.[selectedIndex];
    const activePreview = activeSource?.chunk_content
        ? activeSource.chunk_content.length > 100
            ? `${activeSource.chunk_content.slice(0, 100)}...`
            : activeSource.chunk_content
        : '';

    const popoverContent = (
        <Box sx={{ p: 0.5 }}>
            {/* Source tabs — only render if multiple sources */}
            {numbers.length > 1 && (
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                    {numbers.map((number, i) => (
                        <Box
                            key={number}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                setSelectedIndex(i);
                            }}
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                userSelect: 'none',
                                color: selectedIndex === i ? 'white' : 'var(--color-accent)',
                                bgcolor: selectedIndex === i ? 'var(--color-accent)' : 'transparent',
                                border: '1px solid var(--color-accent)',
                                transition: 'all 0.15s ease',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            {number}
                        </Box>
                    ))}
                </Box>
            )}

            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 'bold',
                    mb: 0.5,
                    color: 'var(--color-accent)',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                }}
            >
                {activeSource?.name || `Source ${activeNumber}`}
            </Typography>

            {activePreview && (
                <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 1, color: 'var(--color-text-secondary)' }}>
                    {activePreview}
                </Typography>
            )}

            {activeSource?.presigned_url ? (
                <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={handleOpenDocViewer(activeSource)}
                    sx={{
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        color: 'var(--color-accent)',
                        borderColor: 'var(--color-accent)',
                        '&:hover': {
                            backgroundColor: 'var(--color-bg-subtle)',
                            borderColor: 'var(--color-accent-hover)',
                            color: 'var(--color-accent-hover)',
                        },
                    }}
                >
                    Read More
                </Button>
            ) : (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'error.main', fontStyle: 'italic' }}>
                        Source link unavailable at this time
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            color: 'text.secondary',
                            wordBreak: 'break-all',
                            fontSize: '0.7rem',
                            mt: 0.25,
                        }}
                    >
                    </Typography>
                </Box>
            )}
        </Box>
    );

    const handleOpenPopover = (e: React.MouseEvent<HTMLElement>) => {
        if (!hasSource) return;
        e.preventDefault();
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');

    return (
        <>
            {isTouchDevice ? (
                <>
                    <span className="citation" onClick={handleOpenPopover}>
                        {badgeText}
                    </span>
                    <Popover
                        open={popoverOpen}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        disableRestoreFocus
                        slotProps={{
                            paper: {
                                sx: {
                                    bgcolor: 'var(--glass-dialog-bg, rgba(255, 255, 255, 0.95))',
                                    backdropFilter: 'blur(10px)',
                                    color: 'var(--color-text, #1a1a1a)',
                                    p: 1.5,
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                    border: '1px solid var(--glass-border, rgba(255, 255, 255, 0.6))',
                                    width: 'calc(100vw - 24px)',
                                    maxWidth: 260,
                                    boxSizing: 'border-box',
                                },
                            },
                        }}
                    >
                        {popoverContent}
                    </Popover>
                </>
            ) : (
                <Tooltip
                    title={hasSource ? popoverContent : ''}
                    arrow
                    placement="top"
                    disableInteractive={false}
                    slotProps={{
                        tooltip: {
                            sx: {
                                bgcolor: 'var(--glass-dialog-bg, rgba(255, 255, 255, 0.95))',
                                backdropFilter: 'blur(10px)',
                                color: 'var(--color-text, #1a1a1a)',
                                p: 1.5,
                                borderRadius: '8px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                border: '1px solid var(--glass-border, rgba(255, 255, 255, 0.6))',
                                maxWidth: 240,
                                pointerEvents: 'auto',
                                '& .MuiTooltip-arrow': {
                                    color: 'var(--glass-dialog-bg, rgba(255, 255, 255, 0.95))',
                                    '&::before': {
                                        border: '1px solid var(--glass-border, rgba(255, 255, 255, 0.6))',
                                    },
                                },
                            },
                        },
                    }}
                >
                    <span className="citation" tabIndex={hasSource ? 0 : undefined}>
                        {badgeText}
                    </span>
                </Tooltip>
            )}
        </>
    );
};